import dayjs, { Dayjs } from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localeData from 'dayjs/plugin/localeData';

import {
  SystemDayOfWeek as DayOfWeek,
  SquirrelMoneyContractsTypesRuleFrequency as RuleFrequency,
  SquirrelMoneyContractsTypesRuleSchedule as RuleSchedule,
  SquirrelMoneyContractsEnumerationsInvestmentAccountRuleTypes as RuleTypes,
} from '@core/schemas/account-overhaul-schema';
import { formatOrdinal } from '@core/utilities/format/number.utility';

dayjs.extend(localeData);
dayjs.extend(advancedFormat);

const RULE_TYPE_LABELS = {
  [RuleTypes.Move]: 'Auto-move',
  [RuleTypes.Withdrawal]: 'Auto-withdraw',
} as const satisfies Record<RuleTypes, string>;

const FREQUENCY_LABELS = {
  [RuleFrequency.Daily]: 'Day',
  [RuleFrequency.Weekly]: 'Week',
  [RuleFrequency.Fortnightly]: 'Fortnight',
  [RuleFrequency.Monthly]: 'Month',
} as const satisfies Record<RuleFrequency, string>;

export const autoRuleTypeLabel = (type: RuleTypes | undefined): string =>
  type === undefined ? '' : RULE_TYPE_LABELS[type];

/** The Frequency dropdown's options, shortest first. */
export const AUTO_RULE_FREQUENCY_OPTIONS = [
  RuleFrequency.Daily,
  RuleFrequency.Weekly,
  RuleFrequency.Fortnightly,
  RuleFrequency.Monthly,
].map(frequency => ({ label: FREQUENCY_LABELS[frequency], value: frequency }));

/** The "Every" value, e.g. "Month". */
export const autoRuleFrequencyLabel = (schedule: RuleSchedule | undefined): string =>
  schedule?.frequency === undefined ? '' : FREQUENCY_LABELS[schedule.frequency];

// dayjs lists weekdays Sunday-first, the same order as the backend's day-of-week enum (Sunday = 0).
const weekdayName = (day: number | undefined): string | null => (day === undefined ? null : dayjs.weekdays()[day]);

/**
 * The "On" value: which day the rule runs, worded as the Frequency's "On" dropdown words it ("Every Monday" for
 * weekly, plain "Monday" for fortnightly, "1st of the month" / "Last day of the month" for monthly). `null` for a
 * daily rule — it runs every day, so there is no day to show.
 */
export function autoRuleOnLabel(schedule: RuleSchedule | undefined): string | null {
  switch (schedule?.frequency) {
    case RuleFrequency.Weekly:
      return schedule.dayOfWeek === undefined ? null : `Every ${weekdayName(schedule.dayOfWeek)}`;
    case RuleFrequency.Fortnightly:
      return weekdayName(schedule.dayOfWeek);
    case RuleFrequency.Monthly:
      if (schedule.lastDayOfMonth) return 'Last day of the month';
      return schedule.dayOfMonth == null ? null : `${formatOrdinal(schedule.dayOfMonth)} of the month`;
    default:
      return null;
  }
}

/** A rule's "On" choice: a day of the week (weekly / fortnightly), a day of the month, or the month's last day. */
export type AutoRuleDay = DayOfWeek | number | 'last';

/** The schedule a Frequency and "On" choice make. A daily rule has no day; a missing choice leaves its field unset. */
export function autoRuleSchedule(frequency: RuleFrequency, day: AutoRuleDay | null): RuleSchedule {
  switch (frequency) {
    case RuleFrequency.Weekly:
    case RuleFrequency.Fortnightly:
      return typeof day === 'number' ? { frequency, dayOfWeek: day } : { frequency };
    case RuleFrequency.Monthly:
      if (day === 'last') return { frequency, lastDayOfMonth: true };
      return typeof day === 'number' ? { frequency, dayOfMonth: day, lastDayOfMonth: false } : { frequency };
    default:
      return { frequency };
  }
}

// Monday first, as the "On" dropdown lists them (the enum itself starts on Sunday).
const DAYS_OF_THE_WEEK = [
  DayOfWeek.Monday,
  DayOfWeek.Tuesday,
  DayOfWeek.Wednesday,
  DayOfWeek.Thursday,
  DayOfWeek.Friday,
  DayOfWeek.Saturday,
  DayOfWeek.Sunday,
];
const DAYS_OF_THE_MONTH: AutoRuleDay[] = [...Array.from({ length: 31 }, (_, i) => i + 1), 'last'];

/** The "On" dropdown's options for a frequency, labelled as the rule will read. None for a daily rule. */
export function autoRuleDayOptions(frequency: RuleFrequency): { label: string; value: AutoRuleDay }[] {
  const days =
    frequency === RuleFrequency.Monthly
      ? DAYS_OF_THE_MONTH
      : frequency === RuleFrequency.Weekly || frequency === RuleFrequency.Fortnightly
        ? DAYS_OF_THE_WEEK
        : [];
  return days.map(day => ({ label: autoRuleOnLabel(autoRuleSchedule(frequency, day)) ?? '', value: day }));
}

/**
 * The first day after `now` that `schedule` runs on, for the "first time this will happen" preview — the backend
 * only reports a rule's next run once it exists. A fortnightly rule is taken to run on the next of its weekday, as
 * there's no earlier run to count a fortnight from. A monthly day the month doesn't have runs on its last day.
 */
export function nextAutoRuleRun(schedule: RuleSchedule, now: Dayjs): Dayjs {
  const today = now.startOf('day');
  switch (schedule.frequency) {
    case RuleFrequency.Weekly:
    case RuleFrequency.Fortnightly: {
      const daysAhead = ((schedule.dayOfWeek ?? DayOfWeek.Monday) - today.day() + 7) % 7 || 7;
      return today.add(daysAhead, 'day');
    }
    case RuleFrequency.Monthly: {
      const runDayIn = (month: Dayjs): Dayjs =>
        schedule.lastDayOfMonth
          ? month.endOf('month').startOf('day')
          : month.date(Math.min(schedule.dayOfMonth ?? 1, month.daysInMonth()));
      const thisMonth = runDayIn(today.startOf('month'));
      return thisMonth.isAfter(today) ? thisMonth : runDayIn(today.add(1, 'month').startOf('month'));
    }
    default:
      return today.add(1, 'day');
  }
}
